/**
 * Deterministic healthcare cover image helper for Heallexa blog articles.
 * Matches category names, slugs, or titles to high-quality Unsplash medical photography.
 */

const CATEGORY_IMAGE_MAP: Record<string, string> = {
  // Primary care / General medicine
  "primary-care": "https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&w=1600&q=80",
  "family-medicine": "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1600&q=80",
  "general-practice": "https://images.unsplash.com/photo-1538108149393-fbbd81895907?auto=format&fit=crop&w=1600&q=80",
  
  // Specialties
  "cardiology": "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?auto=format&fit=crop&w=1600&q=80",
  "heart": "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?auto=format&fit=crop&w=1600&q=80",
  "dentistry": "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1600&q=80",
  "dental": "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1600&q=80",
  "pediatrics": "https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=1600&q=80",
  "children": "https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=1600&q=80",
  "mental-health": "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1600&q=80",
  "counseling": "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1600&q=80",
  "psychiatry": "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1600&q=80",
  "dermatology": "https://images.unsplash.com/photo-1512290900672-1f04e15efdf8?auto=format&fit=crop&w=1600&q=80",
  "skin": "https://images.unsplash.com/photo-1512290900672-1f04e15efdf8?auto=format&fit=crop&w=1600&q=80",
  "ophthalmology": "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=1600&q=80",
  "eye-care": "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=1600&q=80",
  "orthopedic": "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1600&q=80",
  "physical-therapy": "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1600&q=80",
  "womens-health": "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=1600&q=80",
  "obgyn": "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=1600&q=80",
  "urgent-care": "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=1600&q=80",
  "emergency": "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=1600&q=80",
  "telehealth": "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1600&q=80",
  "insurance": "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1600&q=80",
  "billing": "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1600&q=80",
};

const GENERAL_HEALTHCARE_POOL = [
  "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1538108149393-fbbd81895907?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=1600&q=80",
];

export function getBlogCoverImage(article?: {
  slug?: string | null;
  category_slug?: string | null;
  category_name?: string | null;
  title?: string | null;
}): string {
  if (!article) return GENERAL_HEALTHCARE_POOL[0];

  // 1. Check exact category slug
  if (article.category_slug && CATEGORY_IMAGE_MAP[article.category_slug.toLowerCase()]) {
    return CATEGORY_IMAGE_MAP[article.category_slug.toLowerCase()];
  }

  // 2. Check keyword in category name or title or slug
  const text = `${article.category_name ?? ""} ${article.title ?? ""} ${article.slug ?? ""}`.toLowerCase();
  for (const [key, imgUrl] of Object.entries(CATEGORY_IMAGE_MAP)) {
    if (text.includes(key.replace("-", " "))) {
      return imgUrl;
    }
  }

  // 3. Fallback to deterministic hash of the slug/title
  const identifier = article.slug ?? article.title ?? "heallexa-default";
  let hash = 0;
  for (let i = 0; i < identifier.length; i++) {
    hash = (hash << 5) - hash + identifier.charCodeAt(i);
    hash |= 0;
  }
  const index = Math.abs(hash) % GENERAL_HEALTHCARE_POOL.length;
  return GENERAL_HEALTHCARE_POOL[index];
}
